import { Container } from '@acies/core';
import FormInputEvent from './form-input-event';
import InputField from './input-field';
import InputFieldEvent from './input-field-event';
import Label from './label';
import theme from './theme';

/** The FormInput component can be used when we have editable data we want to edit and validate. */
export default class FormInput extends Container {
	public static override observedAttributes: string[] = [...Container.observedAttributes, 'label', 'text', 'text-align', 'readonly', 'required', 'required-error-message', 'validation-error-message', 'regexp', 'key', 'maximum-characters', 'placeholder'];

	/**
	 * The enabled property sets if the FormInput is interactive or not.
	 *
	 * @param {boolean} value True if the FormInput is interactive or false. default is true
	 */
	public set enabled(value: boolean) {
		this.#enabled = value;
		this.#enabledChanged = true;
		this.invalidateProperties();
	}

	public get enabled(): boolean {
		return this.#enabled;
	}

	/** The string value that will be sent with the FormInputEvent when a valid value has changed. */
	public set key(value: string) {
		this.#key = value;
	}

	public get key(): string {
		return this.#key;
	}

	/** The label property sets the text that will be shown above the InputField. */
	public set label(value: string) {
		this.#labelChild.text = value;
	}

	public get label(): string {
		return this.#labelChild.text;
	}

	/**
	 * The maximumCharacters property, sets the maximum allowed characters to be typed or pasted into the Input by the user.
	 *
	 * If you want to "unset" the maximumCharacters, give Number.NaN as the parameter.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/maxLength} for syntax
	 */
	public set maximumCharacters(value: number) {
		if (this.#maximumCharacters === value) {
			return;
		}
		assertIsNonNegativeOrNaN(value);
		this.#maximumCharacters = value;
		this.#maximumCharactersChanged = true;
		this.invalidateProperties();
	}

	public get maximumCharacters(): number {
		return this.#maximumCharacters;
	}

	/**
	 * The placeholder property defines the text displayed in input-field when it has no text.
	 *
	 * The placeholder text should provide a brief hint to the user as to the expected type of data that should be entered into the input-field.
	 */
	public set placeholder(value: string) {
		this.#inputFieldChild.placeholder = value;
	}

	public get placeholder(): string {
		return this.#inputFieldChild.placeholder;
	}

	/** The readOnly property sets if the InputField is editable or not. */
	public set readOnly(value: boolean) {
		this.#inputFieldChild.readOnly = value;
	}

	public get readOnly(): boolean {
		return this.#inputFieldChild.readOnly;
	}

	/** If the regExp property is a RegExp as a string type, the RegExp will test the value when the FormInput is blurred or the Enter key is pressed. */
	public set regExp(value: null | string) {
		if (value === null) {
			this.#regExp = null;
		} else {
			this.#regExp = new RegExp(value);
		}
	}

	public get regExp(): null | string {
		if (this.#regExp) {
			return this.#regExp.source;
		}
		return null;
	}

	/** If set to "true", the Form Input will show the Error state if the value is empty. */
	public set required(value: boolean) {
		if (this.#required === value) {
			return;
		}
		this.#required = value;
		this.#requiredChanged = true;
		this.invalidateProperties();
	}

	public get required(): boolean {
		return this.#required;
	}

	/** The message that will be shown on blur if required is true and the value is an empty string. */
	public set requiredErrorMessage(value: string) {
		this.#requiredErrorMessage = value;
	}

	public get requiredErrorMessage(): string {
		return this.#requiredErrorMessage;
	}

	/** The text content in the InputField child Component. */
	public set text(value: string) {
		this.#inputFieldChild.text = value;
		this.#mesageLabelChild.visibility = false;
		this.#updateMaximumCharactersLabelTextIfValueIsSet();
	}

	public get text(): string {
		return this.#inputFieldChild.text;
	}

	/** The text-align attribute sets the horizontal alignment of the text. */
	public set textAlign(value: 'center' | 'left' | 'right') {
		this.#inputFieldChild.textAlign = value;
	}

	public get textAlign(): 'center' | 'left' | 'right' {
		return this.#inputFieldChild.textAlign;
	}

	/** The message that will be shown on blur if the value does not pass the regex validation. */
	public set validationErrorMessage(value: string) {
		this.#validationErrorMessage = value;
	}

	public get validationErrorMessage(): string {
		return this.#validationErrorMessage;
	}

	#asterixLabelChild: Label;
	#enabled: boolean;
	#enabledChanged: boolean;
	#inputFieldChild: InputField;
	#key: string;
	#labelAsterixContainerChild: Container;
	#labelChild: Label;
	#maximumCharacters: number;
	#maximumCharactersChanged: boolean;
	#maximumCharactersLabelChild!: Label;
	#mesageLabelChild: Label;
	#messageMaximumContainer!: Container;
	#regExp: null | RegExp;
	#required: boolean;
	#requiredChanged: boolean;
	#requiredErrorMessage: string;
	#validationErrorMessage: string;
	public constructor() {
		super();
		this.autoLayout = 'vertical';
		this.gapHorizontal = 8;
		this.gapVertical = 2;
		this.minHeight = 72;
		this.#enabled = true;
		this.#enabledChanged = false;
		this.#labelChild = new Label();
		this.#asterixLabelChild = new Label();
		this.#labelAsterixContainerChild = new Container();
		this.#inputFieldChild = new InputField();
		this.#maximumCharacters = Number.NaN;
		this.#maximumCharactersChanged = false;
		this.#messageMaximumContainer = new Container();
		this.#maximumCharactersLabelChild = new Label();
		this.#mesageLabelChild = new Label();
		this.#required = false;
		this.#requiredChanged = false;
		this.#requiredErrorMessage = '';
		this.#validationErrorMessage = '';
		this.#key = '';
		this.#regExp = null;
		this.#setChildrenProperties();
		this.#addInputFieldEventListeners();
	}

	protected override attributeChangedCallback(name: string, _: string, value: string) {
		super.attributeChangedCallback(name, _, value);
		if (name === 'label') {
			this.#labelAttributeChanged(value);
		} else if (name === 'text') {
			this.#textAttributeChanged(value);
		} else if (name === 'text-align') {
			this.#textAlignAttributeChanged(value);
		} else if (name === 'readonly') {
			this.#readOnlyAttributeChanged(value);
		} else if (name === 'required') {
			this.#requiredAttributeChanged(value);
		} else if (name === 'required-error-message') {
			this.#requiredErrorMessageAttributeChanged(value);
		} else if (name === 'validation-error-message') {
			this.#validationErrorMessageAttributeChanged(value);
		} else if (name === 'regexp') {
			this.#regexpAttributeChanged(value);
		} else if (name === 'key') {
			this.#keyAttributeChanged(value);
		} else if (name === 'maximum-characters') {
			this.#maximumCharactersAttributeChanged(value);
		} else if (name === 'placeholder') {
			this.#placholderAttributeChanged(value);
		}
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#enabledChanged) {
			this.#commitEnabled();
		}
		if (this.#requiredChanged) {
			this.#commitRequired();
		}
		if (this.#maximumCharactersChanged) {
			this.#commitMaximumCharacters();
		}
	}

	protected override initialize(): void {
		super.initialize();
		this.#labelAsterixContainerChild.addComponents([this.#labelChild, this.#asterixLabelChild]);
		this.#messageMaximumContainer.addComponents([this.#mesageLabelChild, this.#maximumCharactersLabelChild]);
		this.addComponents([this.#labelAsterixContainerChild, this.#inputFieldChild, this.#messageMaximumContainer]);
	}

	#addInputFieldEventListeners(): void {
		this.addEventListener(InputFieldEvent.BLURRED, this.#eventsHandler as EventListener);
		this.addEventListener(InputFieldEvent.CHANGED, this.#eventsHandler as EventListener);
		this.addEventListener(InputFieldEvent.ENTER, this.#eventsHandler as EventListener);
		this.addEventListener(InputFieldEvent.FOCUSED, this.#eventsHandler as EventListener);
		this.addEventListener(InputFieldEvent.HOVERED, this.#eventsHandler as EventListener);
		this.addEventListener(InputFieldEvent.UN_HOVERED, this.#eventsHandler as EventListener);
	}

	#commitEnabled(): void {
		this.#enabledChanged = false;
		this.#inputFieldChild.enabled = this.enabled;
	}

	#commitMaximumCharacters(): void {
		this.#maximumCharactersChanged = false;
		this.#inputFieldChild.maximumCharacters = this.#maximumCharacters;
		if (Number.isNaN(this.#maximumCharacters)) {
			this.#maximumCharactersLabelChild.visible = false;
		} else {
			this.#maximumCharactersLabelChild.visible = true;
			this.#maximumCharactersLabelChild.text = `0/${this.#maximumCharacters}`;
		}
	}

	#commitRequired(): void {
		this.#requiredChanged = false;
		this.#asterixLabelChild.visible = this.required;
	}

	#dispatchValidityEvent(): void {
		if (this.required) {
			if (this.#inputFieldChild.text === '') {
				this.dispatchEvent(new FormInputEvent(FormInputEvent.NOT_VALID_VALUE_CHANGED, this.key, this.#inputFieldChild.text));
				return;
			}
		}
		if (this.#regExp) {
			const valid = this.#regExp.test(this.#inputFieldChild.text);
			if (!valid) {
				this.dispatchEvent(new FormInputEvent(FormInputEvent.NOT_VALID_VALUE_CHANGED, this.key, this.#inputFieldChild.text));
				return;
			}
		}
		// both checks are past, so what the field holds is a value a listener can act on. The return
		// above is what makes that true: without it a value the pattern refused would be announced as
		// not valid and then as valid, and the last word would be the wrong one
		this.dispatchEvent(new FormInputEvent(FormInputEvent.VALID_VALUE_CHANGED, this.key, this.#inputFieldChild.text));
	}

	#eventsHandler(event: InputFieldEvent): void {
		event.stopPropagation();
		if (event.type === InputFieldEvent.BLURRED) {
			this.#inputBlurred();
		} else if (event.type === InputFieldEvent.FOCUSED) {
			this.#inputFocused();
		} else if (event.type === InputFieldEvent.CHANGED) {
			this.#inputChanged();
		}
	}

	#inputBlurred(): void {
		this.#validateInputValue();
	}

	#inputChanged(): void {
		this.#updateMaximumCharactersLabelTextIfValueIsSet();
		this.#dispatchValidityEvent();
	}

	#inputFocused(): void {
		this.#mesageLabelChild.visibility = false;
		this.#inputFieldChild.error = false;
		this.#dispatchValidityEvent();
	}

	#keyAttributeChanged(value: string): void {
		this.key = value;
	}

	#labelAttributeChanged(value: string): void {
		this.label = value;
	}

	#maximumCharactersAttributeChanged(value: string): void {
		this.maximumCharacters = Number(value);
	}

	#placholderAttributeChanged(value: string): void {
		this.placeholder = value;
	}

	#readOnlyAttributeChanged(value: string): void {
		if (value === '') {
			this.readOnly = false;
		} else if (value === 'true') {
			this.readOnly = true;
		}
	}

	#regexpAttributeChanged(value: string): void {
		this.regExp = value;
	}

	#requiredAttributeChanged(value: string): void {
		if (value === 'true') {
			this.required = true;
		} else {
			this.required = false;
		}
	}

	#requiredErrorMessageAttributeChanged(value: string) {
		this.requiredErrorMessage = value;
	}

	#setChildrenProperties(): void {
		this.#labelChild.text = 'Label';
		this.#labelChild.variant = 'light';
		this.#asterixLabelChild.visible = false;
		this.#asterixLabelChild.text = '*';
		this.#asterixLabelChild.fill = theme.colors.error.base;
		this.#labelAsterixContainerChild.gapHorizontal = 2;
		this.#inputFieldChild.width = 'fill';
		this.#mesageLabelChild.width = 'fill';
		this.#mesageLabelChild.visibility = false;
		this.#mesageLabelChild.fill = theme.colors.error.base;
		this.#mesageLabelChild.size = 'small';
		this.#maximumCharactersLabelChild.visible = false;
		this.#messageMaximumContainer.width = 'fill';
		this.#messageMaximumContainer.gapHorizontal = 4;
		this.#maximumCharactersLabelChild.size = 'small';
	}

	#textAlignAttributeChanged(value: string): void {
		if (value === 'center' || value === 'left' || value === 'right') {
			this.textAlign = value;
		}
	}

	#textAttributeChanged(value: string): void {
		this.text = value;
	}

	#updateMaximumCharactersLabelTextIfValueIsSet(): void {
		// when writing tests for this, this.#maximumCharacters was always NaN
		// which must be a bug in vitest, since it has the right value during
		// runtime, so I skip the if (!Number.isNaN(this.#maximumCharacters))
		// check and set the value anyways.
		// It's ok because the label is not visible and it will not cause overhead.
		this.#maximumCharactersLabelChild.text = `${this.#inputFieldChild.text.length}/${this.#maximumCharacters}`;
	}

	#validateInputValue(): void {
		this.#mesageLabelChild.visibility = false;
		this.#inputFieldChild.error = false;
		if (this.required) {
			if (this.#inputFieldChild.text === '') {
				this.#mesageLabelChild.visibility = true;
				this.#mesageLabelChild.text = this.requiredErrorMessage;
				this.#inputFieldChild.error = true;
				this.dispatchEvent(new FormInputEvent(FormInputEvent.NOT_VALID_VALUE_CHANGED, this.key, this.#inputFieldChild.text));
				return;
			}
		}
		if (this.#regExp) {
			const valid = this.#regExp.test(this.#inputFieldChild.text);
			if (!valid) {
				this.#mesageLabelChild.text = this.validationErrorMessage;
				this.#mesageLabelChild.visibility = true;
				this.#inputFieldChild.error = true;
				this.dispatchEvent(new FormInputEvent(FormInputEvent.NOT_VALID_VALUE_CHANGED, this.key, this.#inputFieldChild.text));
				return;
			}
		}
		this.dispatchEvent(new FormInputEvent(FormInputEvent.VALID_VALUE_CHANGED, this.key, this.#inputFieldChild.text));
	}

	#validationErrorMessageAttributeChanged(value: string) {
		this.validationErrorMessage = value;
	}
}
customElements.define('ac-form-input', FormInput);

function assertIsNonNegativeOrNaN(value: unknown): asserts value is number {
	if (typeof value === 'number' && (value >= 0 || Number.isNaN(value))) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be a non negative number or Number.NaN`);
}
